import React, { useRef, useState, useContext } from 'react'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Select,
  InputLabel,
  MenuItem,
  FormControl,
  FormHelperText
} from '@mui/material'
import { ModalContainerAnimateDown } from './gsys-ui/ModalContainer/ModalContainer'
import haxios from '../lib/haxios'
import { GlobalContext } from '../context/GlobalContext' // Add this import

const API_URL = 'https://api.aa-vision.com'

const ConfirmationModal = NiceModal.create(({ enquiryId, onCancelComplete }) => {
  console.log('Enquiry ID:', enquiryId)

  const modal = useModal()
  const modalContainer = useRef(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedReason, setSelectedReason] = useState('')
  const [reasonError, setReasonError] = useState(false)
  const { token, username, uinfo, cinfo, apir } = useContext(GlobalContext) // Add this line

  const handleCancelEnquiry = async () => {
    if (!selectedReason) {
      setReasonError(true)
      return
    }

    setLoading(true)
    setError(null)
    setReasonError(false)

    try {
      console.log('Starting fetch...')

      // Step 1: Get enquiry details
      const enquiryDetails = await haxios.get(
        `${API_URL}/api/connectplus/factor/enquiry/${enquiryId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      console.log('Enquiry Details:', enquiryDetails)

      if (!enquiryDetails?.EnquiryId) {
        console.error('Invalid enquiry details:', enquiryDetails)
        throw new Error('Invalid enquiry details received')
      }

      // Step 2: Record cant-supply reason
      const cantSupplyPayload = {
        EnquiryID: enquiryId,
        Reason: selectedReason,
        LoggedBy: cinfo.Username
      }

      console.log('Recording cant-supply reason:', cantSupplyPayload)
      await haxios.post(`${apir}/salesassist/cplusassist/cantsupply`, cantSupplyPayload, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      // Step 3: Cancel the enquiry with the correct payload structure
      console.log('Current enquiry status:', enquiryDetails.Status)
      const cancelPayload = {
        Enquiry: {
          ...enquiryDetails,
          Status: 'Cancelled',
          ResponseHeaderNotes: selectedReason,
          CancelledBy: username || enquiryDetails.Username || enquiryDetails.PortalLockUsername
        },
        ResponseHeader: {
          ResponseHeaderId: crypto.randomUUID(),
          EnquiryId: enquiryId,
          Make: enquiryDetails.Make || null,
          Model: enquiryDetails.Model || null,
          Engine: enquiryDetails.Engine || null,
          Fuel: enquiryDetails.Fuel || null,
          GBox: enquiryDetails.GBox || null,
          Notes: selectedReason,
          TotalCost: 0,
          UserId: enquiryDetails.UserId,
          UserName: username || enquiryDetails.Username || enquiryDetails.PortalLockUsername
        }
      }

      console.log('Posting cancel payload:', cancelPayload)
      await haxios.post(`${API_URL}/api/connectplus/factor/enquiry/cancel`, cancelPayload, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      // Success handling - close immediately
      setLoading(false)
      setError(null)
      setReasonError(false)

      // Close modal immediately on success
      modal.remove()
      if (onCancelComplete) onCancelComplete()
    } catch (err) {
      console.error('Full error:', err)
      setLoading(false)
      setError(err.message || 'An error occurred while canceling the enquiry.')
    }
  }

  const handleClose = () => {
    modal.remove()
  }

  const reasons = [
    'Incorrect reg, please send again.',
    'Main Dealer only.',
    'Listed aftermarket, but not available through MMS.',
    'Account Issue, please call in.',
    'Already been ordered.',
    'Customer Cancelled.'
  ]

  return (
    <ModalContainerAnimateDown ref={modalContainer}>
      <Box
        sx={{
          padding: 3,
          width: '400px',
          backgroundColor: '#34495E',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, color: 'white', fontWeight: 600 }}>
          Are you sure you can't supply this item?
        </Typography>

        <FormControl fullWidth error={reasonError} sx={{ mb: 2 }}>
          <Select
            value={selectedReason}
            onChange={(e) => setSelectedReason(e.target.value)}
            displayEmpty
            sx={{
              'color': 'white',
              'backgroundColor': 'rgba(255, 255, 255, 0.05)',
              'borderRadius': '12px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
                borderWidth: '2px'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#FF6B35'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#FF6B35'
              },
              '& .MuiSvgIcon-root': {
                color: 'white'
              },
              '& .MuiSelect-select': {
                padding: '12px 14px',
                fontSize: '14px',
                fontWeight: '500'
              }
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  'backgroundColor': '#2C3E50',
                  'border': '1px solid rgba(255, 255, 255, 0.2)',
                  'borderRadius': '12px',
                  'boxShadow': '0 8px 32px rgba(0, 0, 0, 0.3)',
                  'marginTop': '8px',
                  '& .MuiMenuItem-root': {
                    'color': 'white',
                    'padding': '12px 16px',
                    'fontSize': '14px',
                    'fontWeight': '500',
                    'borderBottom': '1px solid rgba(255, 255, 255, 0.1)',
                    '&:last-child': {
                      borderBottom: 'none'
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(255, 107, 53, 0.1)',
                      color: 'white'
                    },
                    '&.Mui-selected': {
                      'backgroundColor': 'rgba(255, 107, 53, 0.2)',
                      'color': 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 107, 53, 0.3)'
                      }
                    }
                  }
                }
              }
            }}
          >
            <MenuItem
              value=""
              disabled
              sx={{ color: 'rgba(255, 255, 255, 0.5)', fontStyle: 'italic' }}
            >
              Select a reason
            </MenuItem>
            {reasons.map((reason, index) => (
              <MenuItem key={index} value={reason}>
                {reason}
              </MenuItem>
            ))}
          </Select>
          {reasonError && (
            <FormHelperText sx={{ color: '#E74C3C' }}>Please select a reason</FormHelperText>
          )}
        </FormControl>

        {error && (
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              color: error.includes('successfully') ? '#2ECC71' : '#E74C3C',
              backgroundColor: error.includes('successfully')
                ? 'rgba(46, 204, 113, 0.1)'
                : 'rgba(231, 76, 60, 0.1)',
              padding: '8px 12px',
              borderRadius: '8px',
              border: `1px solid ${error.includes('successfully') ? '#2ECC71' : '#E74C3C'}`,
              fontWeight: '500'
            }}
          >
            {error}
          </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={loading}
            sx={{
              'color': 'white',
              'borderColor': 'rgba(255, 255, 255, 0.3)',
              'borderRadius': '12px',
              'fontWeight': '600',
              'textTransform': 'uppercase',
              'minWidth': '100px',
              '&:hover': {
                borderColor: '#FF6B35',
                backgroundColor: 'rgba(255, 107, 53, 0.1)'
              }
            }}
          >
            NO
          </Button>
          <Button
            variant="contained"
            onClick={handleCancelEnquiry}
            disabled={loading || !selectedReason}
            sx={{
              'backgroundColor': '#E74C3C',
              'color': 'white',
              'borderRadius': '12px',
              'fontWeight': '600',
              'textTransform': 'uppercase',
              'border': '2px solid #E74C3C',
              'boxShadow': '0 2px 8px rgba(231, 76, 60, 0.3)',
              'minWidth': '100px',
              '&:hover': {
                backgroundColor: '#E74C3C',
                borderColor: '#E74C3C',
                boxShadow: '0 4px 12px rgba(231, 76, 60, 0.4)'
              },
              '&:disabled': {
                backgroundColor: 'rgba(231, 76, 60, 0.5)',
                borderColor: 'rgba(231, 76, 60, 0.5)'
              }
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'YES'}
          </Button>
        </Box>
      </Box>
    </ModalContainerAnimateDown>
  )
})

export default ConfirmationModal
